import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { parse } from "query-string";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";

const backendUrl = process.env.REACT_APP_baseUrl;

function firstQueryValue(value: unknown): string {
  if (Array.isArray(value)) {
    return String(value[0] || "");
  }
  return typeof value === "string" ? value : "";
}

function allQueryValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "")).filter(Boolean);
  }
  return typeof value === "string" && value ? [value] : [];
}

/**
 * MCP OAuth Handler
 * 
 * This component handles MCP OAuth flows by checking if the user is logged in
 * to the Console app. If logged in, it completes the OAuth flow. If not, it
 * redirects to the login page and returns here after authentication.
 * 
 * This ensures MCP OAuth reuses the Console app's authentication (email/password
 * or Auth0) without creating a separate identity store.
 * 
 * The JWT is retrieved from Redux state (if available) or localStorage ("user" key).
 * When a user already has a Console session, this reuses that session without
 * triggering a new Auth0 login flow.
 */
const McpOAuth = () => {
  const location = useLocation();
  const user = useSelector((state: any) => state.auth);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("Checking authentication...");
  const inFlight = useRef(false);
  const redirected = useRef(false);

  useEffect(() => {
    const handleMcpOAuth = async () => {
      const params = parse(location.search);
      const sessionValues = allQueryValues(params.session);
      const oauthStateValues = allQueryValues(params.state);
      const mcpSession = firstQueryValue(params.session);
      const oauthState = firstQueryValue(params.state);
      // Prefer our authorize `session=` token. ChatGPT may also put its
      // OAuth CSRF `state` on this URL (or in `session=`). The server tries
      // every candidate, including lookup by client state.
      const lookupKey = mcpSession || oauthState;
      const callback = firstQueryValue(params.callback);

      try {
        if (!lookupKey || !callback) {
          setError("Missing required parameters (session or callback)");
          return;
        }

        // Check if user is logged in (check Redux state first, then localStorage)
        let jwt = user?.jwt;
        
        if (!jwt) {
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              jwt = parsedUser.jwt;
            } catch (parseError) {
              console.error("Failed to parse stored user:", parseError);
            }
          }
        }

        const callbackBody = {
          // MCP session token when present; ChatGPT's OAuth `state` as
          // fallback. Server prefers `session` then looks up by client
          // state so `{state: chatgpt csrf, session: token}` still works.
          session: mcpSession || lookupKey,
          state: oauthState || lookupKey,
          sessions: [...sessionValues, ...oauthStateValues],
        };

        if (!jwt) {
          // Hit MCP even without a Console JWT so prod logs can tell
          // "browser never POSTed" from "posted, no jwt / redirected to login".
          try {
            await axios.post(callback, callbackBody, {
              headers: { "X-Mcp-OAuth-Probe": "no-jwt" },
              validateStatus: () => true,
            });
          } catch {
            // Probe is best-effort; still send the user to login.
          }
        } else {
          // Pending authorizations are single-use. Redux hydrating `user.jwt`
          // must not POST /oauth/callback a second time.
          if (inFlight.current) {
            return;
          }
          inFlight.current = true;

          try {
            setStatus("Completing authentication...");
            const response = await axios.post(
              callback,
              {
                ...callbackBody,
                jwt,
              },
              {
                headers: {
                  Authorization: `Bearer ${jwt}`,
                },
              }
            );

            // Backend returns the OAuth redirect URL (to ChatGPT)
            if (response.data?.redirectUrl) {
              redirected.current = true;
              window.location.href = response.data.redirectUrl;
            } else {
              throw new Error("No redirect URL returned from OAuth callback");
            }
            return;
          } catch (err) {
            if (redirected.current) {
              return;
            }
            // Callback failed - could be invalid JWT or other error
            if (axios.isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
              inFlight.current = false;
              // JWT is invalid, remove stored user and continue to login
              localStorage.removeItem("user");
            } else {
              // Callback failed for other reasons
              throw err;
            }
          }
        }

        // User not authenticated, redirect to login page
        // Store the current URL so we can return after login
        setStatus("Redirecting to login...");
        const returnUrl = `${window.location.pathname}${window.location.search}`;
        localStorage.setItem("mcpOAuthReturnUrl", returnUrl);
        window.location.href = "/";
      } catch (err: any) {
        if (redirected.current) {
          return;
        }
        console.error("MCP OAuth error:", err);
        const serverError = axios.isAxiosError(err)
          ? err.response?.data?.error
          : null;
        if (serverError === "Invalid or expired state token") {
          setError(
            `This ChatGPT Connect session is no longer valid (session ${lookupKey.slice(
              0,
              8
            ) || "unknown"}…). Close this tab — it is often an old mcp-oauth page — then start Connect again from ChatGPT.`
          );
        } else {
          setError(
            serverError || err?.message || "An error occurred during authentication"
          );
        }
      }
    };

    handleMcpOAuth();
  }, [location.search, user?.jwt]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
          px: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
        gap: 2,
      }}
    >
      <CircularProgress color="primary" size={48} />
      <Typography variant="body1" color="text.secondary">
        {status}
      </Typography>
    </Box>
  );
};

export default McpOAuth;
